import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface FuelingData {
  id: number;
  veiculo_id: number;
  motorista_id: number;
  veiculo: string;
  motorista: string;
  data_inicio: string;
  data_fim?: string;
  volume: number;
  custo: number;
  valor_unitario: number;
  odometro: number;
  distancia?: number;
  media_km_litro?: number;
  tipo_combustivel: string;
  posto_nome?: string;
  posto_cnpj?: string;
  posto_uf?: string;
  observacoes?: string;
  status: string;
  criado_em: string;
  atualizado_em?: string;
  veiculo_placa?: string;
  veiculo_modelo?: string;
  veiculo_marca?: string;
  motorista_nome?: string;
  motorista_cpf?: string;
}

const FuelingView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [fueling, setFueling] = useState<FuelingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFueling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadFueling = async () => {
    if (!id) {
      utils.showNotification('ID do abastecimento não informado', { type: 'error' });
      navigate('/abastecimentos');
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.getFueling(Number(id));

      if (response.success && response.data) {
        setFueling(response.data.dados || response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar abastecimento');
      }
    } catch (error: any) {
      console.error('Erro ao carregar abastecimento:', error);
      utils.showNotification(error.message || 'Erro ao carregar abastecimento', { type: 'error' });
      navigate('/abastecimentos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/abastecimentos/editar/${id}`);
  };

  const handleBack = () => {
    navigate('/abastecimentos');
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number | undefined, decimals: number = 2) => {
    if (!value) return '0';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  if (isLoading) {
    return (
      <FormLayout
        title="Visualizar Abastecimento"
        onCancel={handleBack}
        showActions={false}
        isLoading={true}
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando abastecimento...</p>
        </div>
      </FormLayout>
    );
  }

  if (!fueling) {
    return (
      <FormLayout
        title="Visualizar Abastecimento"
        onCancel={handleBack}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>Abastecimento não encontrado</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Visualizar Abastecimento"
      onCancel={handleBack}
      onSave={handleEdit}
      saveButtonText="Editar"
      cancelButtonText="Voltar"
      entityId={fueling.id}
    >
      <div className="form-section">
        <h3 className="form-section-title">Informações Principais</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Veículo</label>
            <div className="read-only-field">
              {fueling.veiculo_placa ? `${fueling.veiculo_placa} - ${fueling.veiculo_modelo || ''}` : fueling.veiculo}
            </div>
          </div>
          <div className="form-group">
            <label>Motorista</label>
            <div className="read-only-field">
              {fueling.motorista_nome || fueling.motorista}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Data/Hora Início</label>
            <div className="read-only-field">
              {formatDateTime(fueling.data_inicio)}
            </div>
          </div>
          <div className="form-group">
            <label>Data/Hora Fim</label>
            <div className="read-only-field">
              {formatDateTime(fueling.data_fim)}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Status</label>
            <div className="read-only-field">
              <span className={`status-badge ${
                fueling.status === 'Concluído' ? 'status-success' :
                fueling.status === 'Pendente' ? 'status-warning' :
                'status-danger'
              }`}>
                {fueling.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Dados do Abastecimento</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Tipo de Combustível</label>
            <div className="read-only-field">{fueling.tipo_combustivel}</div>
          </div>
          <div className="form-group">
            <label>Volume (Litros)</label>
            <div className="read-only-field">{formatNumber(fueling.volume)} L</div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Valor Unitário</label>
            <div className="read-only-field">{formatCurrency(fueling.valor_unitario)}</div>
          </div>
          <div className="form-group">
            <label>Valor Total</label>
            <div className="read-only-field">
              <strong>{formatCurrency(fueling.custo)}</strong>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Odômetro (Km)</label>
            <div className="read-only-field">
              {fueling.odometro ? `${fueling.odometro.toLocaleString('pt-BR')} km` : '-'}
            </div>
          </div>
          <div className="form-group">
            <label>Distância Percorrida</label>
            <div className="read-only-field">
              {fueling.distancia ? `${fueling.distancia.toLocaleString('pt-BR')} km` : '-'}
            </div>
          </div>
        </div>

        {fueling.media_km_litro && (
          <div className="form-row">
            <div className="form-group">
              <label>Média de Consumo</label>
              <div className="read-only-field">
                {formatNumber(fueling.media_km_litro)} km/L
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Dados do Posto</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Nome do Posto</label>
            <div className="read-only-field">{fueling.posto_nome || '-'}</div>
          </div>
          <div className="form-group">
            <label>CNPJ do Posto</label>
            <div className="read-only-field">{fueling.posto_cnpj || '-'}</div>
          </div>
        </div>

        {fueling.posto_uf && (
          <div className="form-row">
            <div className="form-group">
              <label>UF</label>
              <div className="read-only-field">{fueling.posto_uf}</div>
            </div>
          </div>
        )}
      </div>

      {fueling.observacoes && (
        <div className="form-section">
          <h3 className="form-section-title">Observações</h3>
          <div className="form-group">
            <div className="read-only-field" style={{ whiteSpace: 'pre-wrap' }}>
              {fueling.observacoes}
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3 className="form-section-title">Informações de Registro</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Criado em</label>
            <div className="read-only-field">{formatDateTime(fueling.criado_em)}</div>
          </div>
          {fueling.atualizado_em && (
            <div className="form-group">
              <label>Atualizado em</label>
              <div className="read-only-field">{formatDateTime(fueling.atualizado_em)}</div>
            </div>
          )}
        </div>
      </div>
    </FormLayout>
  );
};

export default FuelingView;
